import { useState } from 'react';
import { INSURANCE } from './mockData';
import { SOURCE_LABEL } from './types';
import { fmtDay } from './utils';
import { useInsurance } from './InsuranceContext';
import { SectionHead } from './primitives';
import { PlanSwitch } from './CoverageSections';

const fx = INSURANCE;

const STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  done: { label: 'Done', cls: 'ins-tag-paid' },
  current: { label: 'Current', cls: 'ins-tag-applies' },
  partial: { label: 'Partial', cls: 'ins-tag-pending' },
  scheduled: { label: 'Scheduled', cls: 'ins-tag-notyet' },
};

/** Order by how citable a source is (handoff fix B2): a downloaded document or
 *  this office's own claims outrank a rep's verbal answer on a call. */
const CITABILITY: Record<string, number> = {
  historical_data: 0, stedi: 1, payer_portal: 2, payer_manual: 3, ai_call: 4,
};

/* ── 4.11 How ELVA verified this ─────────────────────────────────────── */
export function VerificationSection() {
  const [plan, setPlan] = useState('sunlife');
  const { openCall, openPortal, scrollTo } = useInsurance();
  const rows = [...(fx.sources[plan] ?? [])].sort((a, b) => (CITABILITY[a.kind] ?? 9) - (CITABILITY[b.kind] ?? 9));

  return (
    <section id="ins-sources" className="ins-section">
      <SectionHead title="How ELVA verified this" right={<PlanSwitch value={plan} onChange={setPlan} />} />
      <p className="ins-note" style={{ marginTop: 0, marginBottom: 12 }}>
        Five sources, ordered by how citable each is when a claim is rejected — this office’s own claims and a downloaded document outrank a rep’s verbal answer on a call. For each fact ELVA keeps the most specific, most recent answer and shows where it came from.
      </p>
      <div className="ins-table-wrap">
        <table className="ins-table">
          <caption className="ins-sr-only">How ELVA verified this, by source</caption>
          <thead>
            <tr>
              <th scope="col">Source</th>
              <th scope="col">Last checked</th>
              <th scope="col">What it gave us</th>
              <th scope="col">Citation</th>
              <th scope="col">Status</th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const s = STATUS_LABEL[r.status];
              return (
                <tr key={r.kind}>
                  <td>
                    <div className="strong">{SOURCE_LABEL[r.kind]}{r.weak ? <span className="ins-weak"> · verbal</span> : null}</div>
                    <div className="ins-sub">{r.what}</div>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{r.lastChecked}</td>
                  <td>{r.gave}{r.weak ? <div className="ins-sub" style={{ color: 'var(--color-warning)' }}>Verbal, not independently documented.</div> : null}</td>
                  <td className="ins-cite" style={{ marginTop: 0 }}>{r.citation ?? '—'}</td>
                  <td><span className={`ins-tag ${s?.cls ?? ''}`}>{s?.label}</span></td>
                  <td>
                    {r.link === 'call' ? <button type="button" className="ins-link" onClick={openCall}>View call</button> : null}
                    {r.link === 'portal' ? <button type="button" className="ins-link" onClick={openPortal}>View download</button> : null}
                    {r.link === 'rules' ? <button type="button" className="ins-link" onClick={() => { scrollTo('ins-rules'); }}>View rules</button> : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="ins-note" style={{ marginTop: 10 }}>Next check: {fmtDay(fx.verification.nextCheck)}, 6:00 AM, the day before the visit.</p>
      <p className="ins-note" style={{ marginTop: 2 }}>ELVA calls or opens the portal only when Stedi, the manual and your history leave a gap.</p>
    </section>
  );
}
